/**
 * NumberFieldType — liczba (całkowita lub ułamkowa). Storage: PG NUMERIC.
 *
 * Akceptuje zarówno liczby całkowite (1, 42) jak i ułamkowe (1.5, 3.14) —
 * większość biznesowych pól typu „number" (godziny, ilości, ceny netto)
 * to floaty. Strict integer można wymusić przez `validation_rules.step` per pole
 * (F12.A2 validation rules editor).
 *
 * Postgres NUMERIC zwracany przez pg driver jako string — walidator i renderer
 * akceptują oba warianty (string z bazy / number z user inputu).
 */

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

function NumberInput({ field, value, onChange, error, disabled, autoFocus }: FieldInputProps) {
  const stringValue =
    typeof value === 'number'
      ? String(value)
      : typeof value === 'string'
        ? value.replace(',', '.')
        : '';
  return (
    <input
      type="number"
      step="any"
      value={stringValue}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') onChange(null);
        else {
          const n = parseFloat(raw);
          onChange(Number.isNaN(n) ? null : n);
        }
      }}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-invalid={!!error}
      data-testid={`field-${field.slug}-input`}
      className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
    />
  );
}

export const NumberFieldType: FieldTypeDefinition = {
  type: 'number',
  renderInput: (props) => <NumberInput {...props} />,
  renderCell: (_field, value) => {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? String(n) : '';
  },
  validate: (value) => {
    if (value === null || value === undefined || value === '') return null;
    // Akceptuj string z postgres NUMERIC (pg driver zwraca jako string).
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? parseFloat(value.replace(',', '.'))
          : Number.NaN;
    if (!Number.isFinite(n)) return 'invalid_number';
    return null;
  },
  defaultValue: null,
};
